/*
 * THE LOCKBOX — sections/lockbox.liquid
 *
 * State comes from the clock and one localStorage flag:
 *   upcoming → gate → open → closed
 *
 * Unlocking is optimistic. The box opens the moment a valid email is submitted;
 * the Klaviyo calls run behind it. The retinol report proved that every step
 * between the form and the offer costs people, and a slow API is a step.
 *
 * Klaviyo (client endpoints, public key only — same contract as klaviyo-subscribe.js):
 *   1. POST /client/subscriptions  → joins the list, consent recorded
 *   2. POST /client/events         → "Unlocked Lockbox" (or "Joined Lockbox Waitlist")
 * The key email and the 2-hours-left reminder trigger on the METRIC.
 *
 * Nothing is sent to Klaviyo from the theme editor, a local `shopify theme dev`
 * preview, or with ?lb_test=1 — testing must not create real profiles.
 */
(function () {
  var COMPANY_ID = 'T2cba4';
  var SUBSCRIBE_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions?company_id=' + COMPANY_ID;
  var EVENT_ENDPOINT = 'https://a.klaviyo.com/client/events?company_id=' + COMPANY_ID;
  var REVISION = '2026-07-15';
  var TYPO_DOMAINS = {
    'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gmal.com': 'gmail.com', 'gamil.com': 'gmail.com',
    'gmail.co': 'gmail.com', 'gmail.con': 'gmail.com', 'gnail.com': 'gmail.com', 'gmaill.com': 'gmail.com',
    'yaho.com': 'yahoo.com', 'yahooo.com': 'yahoo.com', 'yahoo.co': 'yahoo.com', 'yahoo.con': 'yahoo.com',
    'hotmial.com': 'hotmail.com', 'hotmai.com': 'hotmail.com', 'hotmail.co': 'hotmail.com',
    'icloud.co': 'icloud.com', 'iclod.com': 'icloud.com', 'outlok.com': 'outlook.com', 'aol.co': 'aol.com'
  };

  var params = new URLSearchParams(window.location.search);
  var host = window.location.hostname;
  var isTest = params.has('lb_test') || host === '127.0.0.1' || host === 'localhost';

  function store(key, value) {
    try {
      if (value === undefined) return window.localStorage.getItem(key);
      window.localStorage.setItem(key, value);
    } catch (e) { return null; }
  }

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function formatLeft(ms) {
    if (ms < 0) ms = 0;
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400);
    var h = Math.floor((s % 86400) / 3600);
    var m = Math.floor((s % 3600) / 60);
    return (d > 0 ? d + 'd ' : '') + pad(h) + ':' + pad(m) + ':' + pad(s % 60);
  }

  function formatMoney(cents) {
    var v = cents / 100;
    return '$' + (v % 1 === 0 ? v.toFixed(0) : v.toFixed(2));
  }

  function klaviyo(kind, email, section, metric) {
    // test mode: ?lb_test=1, local dev, the theme editor, or the "Send signups to Klaviyo" switch is off
    if (isTest || section.getAttribute('data-design-mode') === 'true' || section.getAttribute('data-klaviyo-live') !== 'true') {
      if (window.console) console.info('[lockbox] test mode — would send', kind, email, metric);
      return;
    }
    var listId = section.getAttribute('data-list-id');
    var run = section.getAttribute('data-run');
    var properties = {
      run: run,
      source: params.get('utm_source') || 'direct',
      campaign: params.get('utm_campaign') || '',
      page: window.location.pathname
    };
    // consent record: region, whether marketing consent was given, and the exact wording they saw
    var eu = section.classList.contains('is-eu');
    properties.consent_region = eu ? 'eu' : 'us-ca';
    properties.marketing_consent = kind === 'subscribe';
    var consentLine = section.querySelector(eu ? '.lockbox__optin span' : '.lockbox__consent--na');
    if (consentLine && kind === 'subscribe') properties.consent_text = consentLine.textContent.replace(/\s+/g, ' ').trim();
    var headers = { 'Content-Type': 'application/vnd.api+json', revision: REVISION };

    var sendEvent = function () {
      return fetch(EVENT_ENDPOINT, {
        method: 'POST',
        keepalive: true,
        headers: headers,
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: properties,
              metric: { data: { type: 'metric', attributes: { name: metric } } },
              profile: { data: { type: 'profile', attributes: { email: email } } }
            }
          }
        })
      });
    };

    // Logged-in customers only get the event: they didn't tick anything, so no consent is recorded for them.
    var subscribe = kind === 'event-only' || !listId
      ? Promise.resolve()
      : fetch(SUBSCRIBE_ENDPOINT, {
          method: 'POST',
          keepalive: true,
          headers: headers,
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: metric === 'Unlocked Lockbox' ? 'Lockbox' : 'Lockbox waitlist',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email: email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } }
                  }
                }
              },
              relationships: { list: { data: { type: 'list', id: listId } } }
            }
          })
        });

    // Both at once, keepalive: the email page navigates to the offer page right after this, and a
    // request chained on the first response would be cancelled by that navigation.
    subscribe.catch(function (error) { if (window.console) console.error('[lockbox] subscribe', error); });
    sendEvent().catch(function (error) { if (window.console) console.error('[lockbox] event', error); });

    if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
      try { window.klaviyo.identify({ email: email }); } catch (e) {}
    }
  }

  // EU + EEA + UK + Switzerland: these visitors get the GDPR checkbox instead of the notice line.
  var EU_COUNTRIES = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
    'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB', 'CH'];

  // Region: starts from the timezone (instant, no flicker for most), then the IP country from
  // Shopify's /browsing_context_suggestions.json. It only ever switches TO the EU version, never
  // away from it — when in doubt, the stricter form. ?lb_region=eu|na forces it in a preview.
  function detectRegion(section, preview) {
    var set = function (on) { section.classList.toggle('is-eu', !!on); };
    var forcedRegion = preview ? params.get('lb_region') : null;
    if (forcedRegion) { set(forcedRegion === 'eu'); return; }
    var tz = '';
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) {}
    var country = (section.getAttribute('data-country') || '').toUpperCase();
    set(EU_COUNTRIES.indexOf(country) > -1 || /^Europe\//.test(tz));
    fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var c = d && d.detected_values && d.detected_values.country && d.detected_values.country.handle;
        if (!c) return;
        c = String(c).toUpperCase();
        section.setAttribute('data-geo', c);
        if (EU_COUNTRIES.indexOf(c) > -1) set(true);
      })
      .catch(function () {});
  }

  function init(section) {
    document.documentElement.classList.add('lockbox-page');
    var openAt = Date.parse(section.getAttribute('data-open-at')) || 0;
    var closeAt = Date.parse(section.getAttribute('data-close-at')) || Infinity;
    var run = section.getAttribute('data-run') || 'lockbox';
    var stayOnPage = true;
    // The key is only ever set by submitting the email form (or a ?key=1 link from the key email).
    // ':email' — keys handed out before 10/01 by the old logged-in-customer shortcut don't count.
    var unlockKey = 'lockbox:' + run + ':email';
    var originalTitle = document.title;
    var toastTimer;

    var countdowns = section.querySelectorAll('[data-countdown]');
    var closeLabels = section.querySelectorAll('[data-close-label]');
    var toast = section.querySelector('.lockbox__toast');
    var summary = section.querySelector('[data-bar-summary]');
    var checkout = section.querySelector('[data-checkout]');

    // Price map for the savings line, from the page's own prices
    // (data-price = crossed-out price, data-lb-price = the price shown on the card).
    var prices = {};
    section.querySelectorAll('.lockbox__card[data-variant-id]').forEach(function (card) {
      prices[card.getAttribute('data-variant-id')] = {
        price: parseInt(card.getAttribute('data-price'), 10) || 0,
        lb: parseInt(card.getAttribute('data-lb-price'), 10) || 0
      };
    });

    if (closeAt !== Infinity) {
      // Always Pacific, written "PT" like the rest of the page (not the visitor's zone, not PDT/PST).
      var label = new Date(closeAt).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' });
      closeLabels.forEach(function (el) { el.textContent = 'Ends ' + label + ' PT'; });
    }

    function isUnlocked() { return store(unlockKey) === '1'; }

    // Preview helpers (theme editor / local / ?lb_test=1): ?lb_state=upcoming|gate|open|closed forces a
    // state — a forced gate still opens once unlocked, so the unlock can be tested — and ?lb_reset=1
    // forgets this browser's unlock.
    var designMode = section.getAttribute('data-design-mode') === 'true';
    var preview = isTest || designMode;
    var forced = preview ? params.get('lb_state') : null;
    // Theme editor: never redirect, and show the editable state whatever the clock or key says —
    // the offer page as open (products, FAQ), the email page as the email form.
    if (designMode && !forced) forced = /^(offer|single)$/.test(section.getAttribute('data-mode')) ? 'open' : 'gate';
    // The ended page (templates/page.lockbox-end.json, /lockbox-end) is always "closed".
    if (section.getAttribute('data-mode') === 'closed') forced = 'closed';
    detectRegion(section, preview);
    // ?closed=1 works on the live page too: a link that always shows "the Lockbox is closed" +
    // the waitlist, whatever the clock says (for SMS / social links shared after the run).
    if (params.get('closed') === '1') forced = 'closed';
    if (preview && params.has('lb_reset')) store(unlockKey, '0');

    // data-mode: "single" = one page (upcoming / gate → open in place / closed). The older two-page
    // set-up: the email page is upcoming / gate / closed, the offer page open / closed.
    var dataMode = section.getAttribute('data-mode');
    var mode = dataMode === 'offer' || dataMode === 'single' ? dataMode : 'gate';

    function currentState() {
      var now = Date.now();
      if (forced) return forced;
      if (now >= closeAt) return 'closed';
      if (mode === 'offer') return 'open';
      if (now < openAt) return 'upcoming';
      return mode === 'single' && isUnlocked() ? 'open' : 'gate';
    }

    // The other page's URL. In a preview (?view=lockbox / ?view=lockbox-open on any page) it swaps
    // the view and keeps the preview params; live, it is the page URL from the section settings.
    function otherPage(extra) {
      var url = new URL(window.location.href);
      if (params.get('view')) {
        url.searchParams.set('view', mode === 'gate' ? 'lockbox-open' : 'lockbox');
      } else {
        url = new URL(section.getAttribute('data-other-page') || (mode === 'gate' ? '/pages/lockbox-open' : '/lockbox'), window.location.origin);
        ['lb_test', 'utm_source', 'utm_medium', 'utm_campaign'].forEach(function (k) {
          if (params.get(k)) url.searchParams.set(k, params.get(k));
        });
      }
      url.searchParams.delete('lb_state');
      url.searchParams.delete('lb_reset');
      Object.keys(extra || {}).forEach(function (k) { url.searchParams.set(k, extra[k]); });
      return url.toString();
    }

    // No discount code is ever put on the cart from here: customers copy LOCKBOX and enter it at
    // checkout themselves (manual coupon), and the products on the page carry their real prices.

    function showToast(message) {
      if (!toast) return;
      toast.textContent = message;
      toast.hidden = false;
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { toast.hidden = true; }, 2200);
    }

    function refreshCart() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var saving = 0;
          cart.items.forEach(function (item) {
            var p = prices[String(item.variant_id)];
            if (p) saving += (p.price - p.lb) * item.quantity;
          });
          if (!cart.item_count) {
            summary.textContent = 'Your cart is empty';
            checkout.setAttribute('aria-disabled', 'true');
          } else {
            summary.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items') +
              (saving > 0 ? ' · saving ' + formatMoney(saving) : '');
            checkout.removeAttribute('aria-disabled');
          }
        })
        .catch(function () {});
    }

    // Boxed clock: DAYS (only while > 0) / HOURS / MIN / SEC.
    function setBlocks(el, ms) {
      if (ms < 0) ms = 0;
      var t = Math.floor(ms / 1000);
      var units = { d: Math.floor(t / 86400), h: Math.floor((t % 86400) / 3600), m: Math.floor((t % 3600) / 60), s: t % 60 };
      Object.keys(units).forEach(function (u) {
        var b = el.querySelector('[data-u="' + u + '"]');
        if (b) b.textContent = u === 'd' ? units.d : pad(units[u]);
      });
      el.classList.toggle('has-days', units.d > 0);
    }

    // Someone who reserved before opening sees "reserved" instead of the form again.
    function showReserved() {
      section.querySelectorAll('[data-lockbox-form="reserve"]').forEach(function (form) {
        var row = form.querySelector('.lockbox__row');
        var ok = form.querySelector('.lockbox__success');
        if (row) row.hidden = true;
        if (ok) ok.hidden = false;
      });
    }

    var lastState = null;
    function render() {
      var state = currentState();
      if (state !== lastState) {
        section.setAttribute('data-state', state);
        // one page: offer layout (sticky bar padding, key size) only once it is open
        if (mode === 'single') section.classList.toggle('lockbox--offer', state === 'open');
        if (state === 'open') refreshCart();
        if (state === 'upcoming' && isUnlocked()) showReserved();
        lastState = state;
      }
      var now = Date.now();
      countdowns.forEach(function (el) {
        var target = el.getAttribute('data-countdown') === 'open' ? openAt : closeAt;
        if (el.getAttribute('data-style') === 'blocks') setBlocks(el, target === Infinity ? 0 : target - now);
        else el.textContent = target === Infinity ? '' : formatLeft(target - now);
        var timer = el.closest('.lockbox__timer');
        if (timer) timer.classList.toggle('is-final', target - now < 3600000);
      });
    }

    // One page: the Lockbox opens right here. Email page: on to the Lockbox page — clean URL when
    // this browser remembers the unlock; ?key=1 only rides along if localStorage is blocked, so the
    // Lockbox page still lets them in (no redirect loop). Klaviyo calls use keepalive, so leaving
    // the page right away doesn't cancel them.
    function unlock(email, kind) {
      store(unlockKey, '1');
      if (email) klaviyo(kind, email, section, 'Unlocked Lockbox');
      section.classList.add('is-unlocking');
      if (mode === 'single') {
        // a previewed gate (?lb_state=gate), or a browser that can't remember the key: open it
        // for this visit
        if (forced === 'gate' || !isUnlocked()) forced = 'open';
        render();
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      // a previewed email page (?lb_state=…) lands on a previewed Lockbox page, whatever the clock says
      var extra = forced ? { key: '1', lb_state: 'open' } : (isUnlocked() ? {} : { key: '1' });
      if (!designMode) setTimeout(function () { window.location.href = otherPage(extra); }, 400);
    }

    if (mode === 'offer' || mode === 'single') {
      // ?key=1 comes from the email page and from the key / reminder emails: they gave their email.
      if (params.get('key') === '1') store(unlockKey, '1');
      // Tidy the address bar: once the key is remembered, drop ?key=1 (kept if storage is blocked).
      if (params.has('key') && isUnlocked() && window.history.replaceState) {
        var clean = new URL(window.location.href);
        clean.searchParams.delete('key');
        window.history.replaceState(window.history.state, '', clean.pathname + clean.search + clean.hash);
      }
      // No key yet, or it hasn't opened: back to the email page (never from a forced preview state).
      if (mode === 'offer' && !forced && (Date.now() < openAt || (!isUnlocked() && Date.now() < closeAt))) {
        window.location.replace(otherPage());
        return;
      }
    }
    // Email page: everyone enters their email, every time — no skipping for logged-in customers
    // or for visitors who unlocked before.

    // ------------------------------------------------ forms
    section.querySelectorAll('[data-lockbox-form]').forEach(function (form) {
      var input = form.querySelector('input[type="email"]');
      var error = form.querySelector('.lockbox__error');
      var hint = form.querySelector('[data-typo]');
      var fix = form.querySelector('[data-typo-fix]');
      var success = form.querySelector('.lockbox__success');
      var mode = form.getAttribute('data-lockbox-form');

      function suggestion(value) {
        var at = value.lastIndexOf('@');
        if (at < 1) return null;
        var domain = value.slice(at + 1).toLowerCase();
        return TYPO_DOMAINS[domain] ? value.slice(0, at + 1) + TYPO_DOMAINS[domain] : null;
      }

      input.addEventListener('blur', function () {
        var s = suggestion(input.value.trim());
        hint.hidden = !s;
        if (s) fix.textContent = s;
      });
      fix.addEventListener('click', function () {
        input.value = fix.textContent;
        hint.hidden = true;
        input.focus();
      });

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var email = input.value.trim();
        if (!email || !input.checkValidity() || email.indexOf('.') === -1) {
          error.textContent = 'Please enter a valid email, like you@email.com.';
          error.hidden = false;
          input.setAttribute('aria-invalid', 'true');
          input.focus();
          return;
        }
        // EU: marketing consent only if the (unticked by default) box is ticked
        var optin = form.querySelector('[data-optin]');
        var eu = section.classList.contains('is-eu');
        var marketing = !eu || (optin && optin.checked);
        if (mode === 'waitlist' && !marketing) {
          // the waitlist IS a request for emails: without the tick there is nothing to send
          error.textContent = 'Tick the box so we can email you when the next Lockbox opens.';
          error.hidden = false;
          return;
        }
        var kind = marketing ? 'subscribe' : 'event-only';

        error.hidden = true;
        input.removeAttribute('aria-invalid');
        input.blur();

        if (mode === 'unlock') {
          unlock(email, kind);
        } else if (mode === 'reserve') {
          // Before opening: the key is saved on this device and the unlock is recorded, so the
          // offer opens for them the second the clock reaches open_at.
          store(unlockKey, '1');
          klaviyo(kind, email, section, 'Unlocked Lockbox');
          showReserved();
        } else {
          klaviyo('subscribe', email, section, 'Joined Lockbox Waitlist');
          form.querySelector('.lockbox__row').hidden = true;
          form.querySelectorAll('.lockbox__consent, .lockbox__optin').forEach(function (el) { el.hidden = true; });
          success.hidden = false;
        }
      });
    });

    // ------------------------------------------------ copy code
    section.querySelectorAll('[data-copy]').forEach(function (button) {
      button.addEventListener('click', function () {
        var code = button.getAttribute('data-copy');
        if (navigator.clipboard) navigator.clipboard.writeText(code).catch(function () {});
        button.textContent = 'Copied';
        setTimeout(function () { button.textContent = 'Copy'; }, 1800);
      });
    });

    // ------------------------------------------------ category tabs
    section.querySelectorAll('.lockbox__tab').forEach(function (tab) {
      tab.addEventListener('click', function () {
        var filter = tab.getAttribute('data-filter');
        section.querySelectorAll('.lockbox__tab').forEach(function (t) {
          var active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        section.querySelectorAll('.lockbox__grid--catalog .lockbox__card').forEach(function (card) {
          card.hidden = filter !== 'all' && card.getAttribute('data-category') !== filter;
        });
      });
    });

    // ------------------------------------------------ add to cart
    // Rebuy's Smart Cart opens its flyout on every /cart/add.js ("rebuy:smartcart.show"). On this
    // page it covers the offer with full prices and unrelated upsells, so a flyout that opens
    // within 3s of an add made HERE is closed again; the sticky bar carries the cart instead.
    // Opening the cart from the header icon is untouched.
    var suppressFlyoutUntil = 0;
    function closeRebuyFlyout() {
      if (!stayOnPage || Date.now() > suppressFlyoutUntil) return;
      var cart = window.Rebuy && window.Rebuy.SmartCart;
      if (cart && typeof cart.hide === 'function') {
        try { cart.hide(); } catch (e) {}
      }
    }
    document.addEventListener('rebuy:smartcart.show', closeRebuyFlyout);
    window.addEventListener('rebuy:smartcart.show', closeRebuyFlyout);

    section.addEventListener('click', function (event) {
      var button = event.target.closest('[data-add]');
      if (!button || !section.contains(button)) return;
      var card = button.closest('.lockbox__card');
      button.disabled = true;
      suppressFlyoutUntil = Date.now() + 3000;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(card.getAttribute('data-variant-id'), 10), quantity: 1 }] })
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not add'); });
          return r.json();
        })
        .then(function () {
          button.classList.add('is-added');
          button.textContent = 'Added ✓';
          showToast('Added to your cart');
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

          return refreshCart();
        })
        .catch(function (error) {
          showToast(error.message || 'Could not add — please try again');
        })
        .then(function () {
          setTimeout(function () {
            button.disabled = false;
            button.classList.remove('is-added');
            button.textContent = 'Add to cart';
          }, 1600);
        });
    });

    // Sticky bar before unlocking: jump to the form that is showing and put the cursor in it.
    var barUnlock = section.querySelector('[data-bar-unlock]');
    if (barUnlock) {
      barUnlock.addEventListener('click', function () {
        var input = section.querySelector('.lockbox__panel--' + lastState + ' input[type="email"]');
        if (!input) return;
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(function () { input.focus({ preventScroll: true }); }, 400);
      });
    }

    // ------------------------------------------------ tab title while they're away
    // Refreshed on every tick, not only when the tab is hidden: browsers keep running this timer
    // in a background tab (about once a minute), so the countdown in the tab keeps moving.
    function updateTitle() {
      var live = lastState === 'open' || lastState === 'gate';
      if (document.hidden && live && closeAt !== Infinity) {
        var left = Math.max(0, closeAt - Date.now());
        var h = Math.floor(left / 3600000);
        var m = Math.floor((left % 3600000) / 60000);
        // short: a background tab shows ~15 characters — the time must come first
        var t = '🔑 ' + (h > 0 ? h + 'h ' : '') + m + 'm left';
        if (document.title !== t) document.title = t;
      } else if (document.title !== originalTitle) {
        document.title = originalTitle;
      }
    }
    document.addEventListener('visibilitychange', updateTitle);

    render();
    setInterval(function () { render(); updateTitle(); }, 1000);
  }

  function boot() {
    document.querySelectorAll('.lockbox').forEach(function (section) {
      if (section.__lockbox) return;
      section.__lockbox = true;
      init(section);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
